import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Button, Checkbox, Flex, Text, TextField } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import toast from "react-hot-toast";
import HighZIndexDialog from "@/components/ui/HighZIndexDialog";
import ImagePickerField from "@/components/ui/ImagePickerField";
import PushButtonsField from "@/components/ui/PushButtonsField";
import IosButtonGroupPicker from "@/components/ui/IosButtonGroupPicker";
import {
  PushTemplate,
  PushTemplatesResponse,
  SaveTemplateInput,
  tenantPushService,
} from "@/services/breeze/TenantPushService";
import { PushTemplatePreset } from "./pushPresets";
import { messageFromError, slugify } from "@/utils/text";

interface Props {
  initial: PushTemplate | null;
  presetInitial?: PushTemplatePreset | null;
  onClose: () => void;
  onSaved: (saved: PushTemplate) => void;
}

export default function PushTemplateEditorDialog({
  initial,
  presetInitial,
  onClose,
  onSaved,
}: Props) {
  const isNew = !initial;
  const [draft, setDraft] = useState<SaveTemplateInput>(() => {
    if (presetInitial) {
      const t = presetInitial.template;
      return {
        template_type: t.template_type,
        name: t.name,
        description: t.description ?? "",
        title: t.title,
        body: t.body,
        image_url: t.image_url ?? "",
        click_action: t.click_action ?? "",
        category: t.category ?? "",
        buttons: t.buttons?.map((b) => ({
          id: b.id,
          label: b.label,
          click_action: b.click_action ?? undefined,
        })) ?? [],
        is_active: true,
      };
    }
    return {
      template_type: initial?.template_type ?? "",
      name: initial?.name ?? "",
      description: initial?.description ?? "",
      title: initial?.title ?? "",
      body: initial?.body ?? "",
      image_url: initial?.image_url ?? "",
      click_action: initial?.click_action ?? "",
      category: initial?.category ?? "",
      buttons: initial?.buttons?.map((b) => ({
        id: b.id,
        label: b.label,
        click_action: b.click_action ?? undefined,
      })) ?? [],
      is_active: initial?.is_active ?? true,
    };
  });

  const [templateTypeDirty, setTemplateTypeDirty] = useState(!isNew);

  const setNameWithSlug = (name: string) => {
    setDraft((d) => ({
      ...d,
      name,
      template_type: isNew && !templateTypeDirty ? slugify(name) : d.template_type,
    }));
  };

  const setTemplateTypeManually = (value: string) => {
    setTemplateTypeDirty(true);
    setDraft((d) => ({ ...d, template_type: slugify(value) }));
  };

  const { data: categoriesData } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
  });
  const selectedGroup = (categoriesData?.categories ?? []).find(
    (c) => c.category === draft.category,
  );
  const lockedByGroup = selectedGroup && (selectedGroup.ios_action_order?.length ?? 0) > 0
    ? (selectedGroup.label || selectedGroup.category)
    : null;

  const queryClient = useQueryClient();
  const saveMutation = useMutation({
    mutationFn: () => isNew
      ? tenantPushService.createTemplate(draft)
      : tenantPushService.updateTemplate(initial!.id, draft),
    onSuccess: (saved) => {
      // Prime cache so consumer Selects can resolve template_type before invalidate refetches.
      queryClient.setQueryData<PushTemplatesResponse | undefined>(
        ["push-templates"],
        (prev) => {
          if (!prev) return { templates: [saved] };
          const others = prev.templates.filter((t) => t.id !== saved.id);
          return { ...prev, templates: [...others, saved] };
        },
      );
      queryClient.invalidateQueries({ queryKey: ["push-templates"] });
      toast.success(isNew ? "Template created." : "Template updated.");
      onSaved(saved);
    },
    onError: (e: unknown) => toast.error(messageFromError(e, "Could not save template")),
  });

  return (
    <HighZIndexDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={isNew ? "New push template" : `Edit '${initial?.template_type}'`}
    >
      <Flex direction="column" gap="3" mt="3">
          <Flex gap="3" wrap="wrap">
            <Box style={{ flex: "1 1 240px" }}>
              <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>Display name</Text>
              <TextField.Root
                value={draft.name ?? ""}
                onChange={(e) => setNameWithSlug(e.target.value)}
                placeholder="Workout reminder"
              />
              <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
                What you'll see in dropdowns and lists.
              </Text>
            </Box>
            <Box style={{ flex: "1 1 240px" }}>
              <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
                Template id <Text color="red">*</Text>
              </Text>
              <TextField.Root
                value={draft.template_type}
                onChange={(e) => setTemplateTypeManually(e.target.value)}
                placeholder="workout_reminder"
                disabled={!isNew}
              />
              <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
                {isNew
                  ? templateTypeDirty
                    ? "Workflow steps reference this id. Auto-fill disabled — clear to re-enable."
                    : "Auto-filled from the display name. Edit to take manual control."
                  : "Immutable once created — workflow steps reference this id."}
              </Text>
            </Box>
          </Flex>
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>Description</Text>
            <TextField.Root
              value={draft.description ?? ""}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Sent at the start of a scheduled training session"
            />
          </Box>
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Title <Text color="red">*</Text>
            </Text>
            <TextField.Root
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Time to train"
            />
          </Box>
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Body <Text color="red">*</Text>
            </Text>
            <TextField.Root
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder="Your {{ $anythink.trigger.data.session_name }} session is queued."
            />
          </Box>
          <ImagePickerField
            value={draft.image_url ?? ""}
            onChange={(v) => setDraft({ ...draft, image_url: v ?? "" })}
            label="Image (optional)"
            allowManualUrl
          />
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>Deep link when tapped (optional)</Text>
            <TextField.Root
              value={draft.click_action ?? ""}
              onChange={(e) => setDraft({ ...draft, click_action: e.target.value })}
              placeholder="/workout/{{ $anythink.trigger.data.workout_id }}"
            />
          </Box>
          <IosButtonGroupPicker
            value={draft.category ?? ""}
            onChange={(v) => setDraft({ ...draft, category: v })}
            buttons={draft.buttons ?? []}
            onAutofillButtons={(next) => setDraft({ ...draft, buttons: next })}
          />
          <PushButtonsField
            value={draft.buttons ?? []}
            onChange={(next) => setDraft({ ...draft, buttons: next })}
            lockedByGroup={lockedByGroup}
          />
          <Flex align="center" gap="2">
            <Checkbox
              checked={draft.is_active}
              onCheckedChange={(v) => setDraft({ ...draft, is_active: v === true })}
            />
            <Text size="2">Active (workflows referencing this template will resolve)</Text>
        </Flex>
      </Flex>
      <Flex gap="2" justify="end" mt="4">
        <Dialog.Close asChild>
          <Button type="button" variant="soft" color="gray">Cancel</Button>
        </Dialog.Close>
        <Button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !draft.template_type || !draft.title || !draft.body}
        >
          {saveMutation.isPending ? "Saving…" : isNew ? "Create template" : "Save changes"}
        </Button>
      </Flex>
    </HighZIndexDialog>
  );
}
